// firebaseConfig.js
import { initializeApp } from 'firebase/app';
import { getAuth } from 'firebase/auth';
import { initializeFirestore } from 'firebase/firestore';
import { getStorage } from 'firebase/storage';
import { getFunctions, httpsCallable } from 'firebase/functions';
import { getDatabase } from 'firebase/database';

function normalizeUrlBase(value, fallback) {
  return String(value || fallback || '').trim().replace(/\/+$/, '');
}

// Firebase configuration
const DEFAULT_FIREBASE_CONFIG = {
  apiKey: 'AIzaSyBnduMwQEDazFk2gKhTIL17FTV2qw9dweM',
  appId: '1:753124254476:web:533648cdbf7f50464fdf29',
  authDomain: 'orbital-live.firebaseapp.com',
  databaseURL: 'https://orbital-live-default-rtdb.firebaseio.com/',
  measurementId: 'G-438JHY59RY',
  messagingSenderId: '753124254476',
  projectId: 'orbital-live',
  storageBucket: 'orbital-live.firebasestorage.app',
};

const firebaseConfig = {
  apiKey: process.env.REACT_APP_FIREBASE_API_KEY || DEFAULT_FIREBASE_CONFIG.apiKey,
  authDomain: process.env.REACT_APP_FIREBASE_AUTH_DOMAIN || DEFAULT_FIREBASE_CONFIG.authDomain,
  projectId: process.env.REACT_APP_FIREBASE_PROJECT_ID || DEFAULT_FIREBASE_CONFIG.projectId,
  storageBucket: process.env.REACT_APP_FIREBASE_STORAGE_BUCKET || DEFAULT_FIREBASE_CONFIG.storageBucket,
  messagingSenderId: process.env.REACT_APP_FIREBASE_MESSAGING_SENDER_ID || DEFAULT_FIREBASE_CONFIG.messagingSenderId,
  appId: process.env.REACT_APP_FIREBASE_APP_ID || DEFAULT_FIREBASE_CONFIG.appId,
  measurementId: process.env.REACT_APP_FIREBASE_MEASUREMENT_ID || DEFAULT_FIREBASE_CONFIG.measurementId,
  databaseURL: process.env.REACT_APP_FIREBASE_DATABASE_URL || DEFAULT_FIREBASE_CONFIG.databaseURL,
};

const FIREBASE_FUNCTIONS_REGION = process.env.REACT_APP_FIREBASE_FUNCTIONS_REGION || 'us-central1';
const FIREBASE_PROJECT_ID = firebaseConfig.projectId;
const APP_BASE_URL = normalizeUrlBase(
  process.env.REACT_APP_APP_BASE_URL || process.env.REACT_APP_PUBLIC_APP_ORIGIN,
  'https://app.appdeck.ai'
);
const FUNCTIONS_BASE_URL = normalizeUrlBase(
  process.env.REACT_APP_FUNCTIONS_BASE_URL,
  `https://${FIREBASE_FUNCTIONS_REGION}-${FIREBASE_PROJECT_ID}.cloudfunctions.net`
);

function buildFunctionUrl(functionName) {
  const cleanedFunctionName = String(functionName || '').trim().replace(/^\/+/, '');
  return `${FUNCTIONS_BASE_URL}/${cleanedFunctionName}`;
}

// 🔊 add this line:
//setLogLevel('debug');

// Initialize Firebase
const app = initializeApp(firebaseConfig);

// Initialize Firebase Authentication
const auth = getAuth(app);

// Initialize Firestore
const db = initializeFirestore(app, {
  experimentalAutoDetectLongPolling: true,
  useFetchStreams: false,
});

// Initialize Firebase Storage
const storage = getStorage(app);

// Initialize Firebase Functions
const functions = getFunctions(app, FIREBASE_FUNCTIONS_REGION);

// Initialize Firebase Realtime Database
const realTimeDb = getDatabase(app);

// Centralized collection names
const collections = {
  automations: "_automations",
  appInstances: "_app_instances",
  checklists: "_checklists",
  checklistRecords: "_checklist_runs",
  mentionedPeople: "_mentioned_people",
  collections: "_collections",
  dashboards: "_dashboards",
  library: "_library",
  edges: "_edges",
  events: "_events",
  engagements: "_engagements",
  icons: "_icons",
  layouts: "_layouts",
  forms: "_forms",
  pins: "_pins",
  nodes: "_nodes",
  nodetypes: "_nodetypes",
  nodeChangeNotificationBatches: "_node_change_notification_batches",
  notifications: "_notifications",
  personalReminders: "_reminders",
  pushSubscriptions: "_push_subscriptions",
  plans: "_plans",
  productLines: "_product_lines",
  productLinePacks: "_product_line_packs",
  purchases: "_purchases",
  redemptions: "_redemptions",
  settings: "_settings",
  songs: "_songs",
  storagelogs: "_storagelogs",
  streamlogs: "_streamlogs",
  subscriptions: "_subscriptions",
  tickets: "_tickets",
  nodeviews: "_nodeviews",
  views: "_views",
  /** Base-scoped workflow templates (system types + optional overlays). */
  workflowTemplates: "_workflowTemplates",
  /** Subcollection name under `_nodetypes/{typeKey}/` for system view templates. */
  nodeTypeViews: "views",
  /** Subcollection name under `_nodetypes/{typeKey}/` for system automation templates. */
  nodeTypeAutomations: "automations",
  /** Subcollection name under `_nodetypes/{typeKey}/` for system form templates. */
  nodeTypeForms: "forms",
  /** Subcollection name under `_nodetypes/{typeKey}/` for platform catalog entries. */
  nodeTypeCatalog: "_catalog",
  /** Platform help root. Articles live under `_help/global/articles`. */
  help: "_help",
  helpAppDoc: "global",
  /** Subcollection name for help articles. */
  helpArticles: "articles",
  nodeTypeArticles: "articles",
  bases: "_bases",
  baseBilling: "_base_billing",
  baseInvites: "_base_invites",
  baseInviteTokens: "_base_invite_tokens",
  poSignInvites: "_po_sign_invites",
  poSignInviteTokens: "_po_sign_invite_tokens",
  qboConnections: "_qbo_connections",
  qboOauthNonces: "_qbo_oauth_nonces",
  qboSyncJobs: "_qbo_sync_jobs",
  platformEconomics: "_platform_economics",
  platformEconomicsConfig: "_platform_economics_config",
  platformAi: "_platform_ai",
  aiRuns: "_ai_runs",
  aiUsage: "_ai_usage",
  aiFeedback: "_ai_feedback",
  nodeKnowledge: "_node_knowledge",
  nodeKnowledgeQueue: "_node_knowledge_queue",
  suggestedGroups: "suggestedGroups",
  groupingFeatures: "groupingFeatures",
  watches: "_watches",
  // Must not be "members" — that name is reserved for base/user membership CG rules.
  watchMembers: "memberships",
  /** Subcollection under `_bases/{baseKey}/` — project floor plan layout docs. */
  projectFloorPlans: "projectFloorPlans",
  store: "_store",
  studioApps: "_studio_apps",
  themePacks: "_theme_packs",
};

export {
  APP_BASE_URL,
  FIREBASE_PROJECT_ID,
  FIREBASE_FUNCTIONS_REGION,
  FUNCTIONS_BASE_URL,
  app,
  auth,
  buildFunctionUrl,
  collections,
  db,
  functions,
  httpsCallable,
  realTimeDb,
  storage,
};
